import cn from 'classnames';
import { FC } from 'react';
import { useIntl } from 'react-intl';

import { ensureRefreshImageSrc } from '@/entities/assets';

import {
  getReportPreviewTestIDSegment,
  ReportCardLayout,
  StructuredReportsAndStudiesType,
} from '@/features/reportCards';

import { useReportCard, useReportCardActions } from '../../hooks';
import { sanitizeForTestID } from '../../lib';
import { DetailsPopover } from '../DetailsPopover/DetailsPopover';
import { ReportCardFooter } from '../ReportCardFooter/ReportCardFooter';
import { ReportGenerationStatus } from '../ReportGenerationStatus/ReportGenerationStatus';

type ReportCardProps = {
  structuredReportsAndStudies: StructuredReportsAndStudiesType;
  className?: string;
  testID?: string;
};

export const ReportCard: FC<ReportCardProps> = (props) => {
  const { structuredReportsAndStudies, className, testID } = props;

  const { formatMessage, formatDate } = useIntl();
  const reportCard = useReportCard(structuredReportsAndStudies);
  const {
    isReportGenerationCompleted,
    isIOXRayOrPanowings,
    reportType,
    date,
    report,
    isReportFailed,
    isRegenerateAvailable,
    isReportSigned,
    isReportOrderAvailable,
    reorderFunding,
    isLoadingPDF,
    cardPreviewSrc,
    isIntraoralScanReport,
    shouldHideDownload,
    reportTitle,
    reportLink,
    handleCardClick,
    handleCardAuxClick,
    reorderReport,
    downloadReportHandler,
  } = reportCard;

  const {
    isRemoving,
    isReportHidden,
    enableArchiveReportsFeature,
    CanArchive,
    CanDelete,
    CanRestore,
    hasMobileActions,
    handleRemoveWithAnimation,
    handleHideWithAnimation,
    handleRestoreWithAnimation,
    openReportInfoModal,
  } = useReportCardActions({ structuredReportsAndStudies, reportCard });

  if (!report) {
    return;
  }

  const reportPreviewTestIDSegment = getReportPreviewTestIDSegment(reportType);

  return (
    <ReportCardLayout
      className={className}
      date={
        date
          ? formatDate(date, {
              dateStyle: 'medium',
            })
          : formatMessage({
              id: 'reportCard.unknownDate',
              defaultMessage: 'Unknown date',
            })
      }
      desktopActions={
        <DetailsPopover
          reportID={report.ID}
          sourceStudyIDs={report.SourceStudyIDs}
          studies={structuredReportsAndStudies.studies ?? []}
          canArchive={CanArchive}
          canDelete={CanDelete}
          canRestore={CanRestore}
          hideMode={enableArchiveReportsFeature}
          isHidden={isReportHidden}
          isReportGenerationCompleted={Boolean(isReportGenerationCompleted)}
          isLoadingPDF={isLoadingPDF}
          shouldHideDownload={shouldHideDownload}
          onDetailsView={openReportInfoModal}
          onRemoveReport={handleRemoveWithAnimation}
          onDownloadReport={downloadReportHandler}
          onHideReport={handleHideWithAnimation}
          onRestoreReport={handleRestoreWithAnimation}
        />
      }
      isGenerationCompleted={Boolean(isReportGenerationCompleted)}
      isHidden={isReportHidden}
      isRemoving={isRemoving}
      to={reportLink?.to}
      href={reportLink?.href}
      onAuxClick={handleCardAuxClick}
      onClick={handleCardClick}
      onImageError={ensureRefreshImageSrc}
      onMobileActionsClick={
        hasMobileActions
          ? (event) => {
              event.stopPropagation();
              openReportInfoModal();
            }
          : undefined
      }
      previewAlt={reportTitle}
      previewClassName={cn(reportType)}
      previewSrc={cardPreviewSrc}
      previewTestID={
        reportPreviewTestIDSegment
          ? `preview-report-${sanitizeForTestID(
              reportPreviewTestIDSegment,
            )}-${report.ID}`
          : undefined
      }
      shouldContainPreview={isIOXRayOrPanowings}
      shouldUseLightBackground={isIntraoralScanReport}
      testID={testID}
      title={reportTitle}
    >
      {!isReportGenerationCompleted && (
        <ReportGenerationStatus report={report} />
      )}

      <ReportCardFooter
        isReportFailed={Boolean(isReportFailed)}
        isReportSigned={Boolean(isReportSigned)}
        reorderFunding={reorderFunding}
        canReorder={Boolean(isRegenerateAvailable)}
        isReorderDisabled={
          isReportOrderAvailable && reorderFunding.source !== 'diagnocoins'
        }
        onReorder={reorderReport}
      />
    </ReportCardLayout>
  );
};
